
import { LinkTypeEnum, ParserFieldObject, Property } from '@/types';
import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch,
} from 'vue';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import RichTextingCtaItem from '@/components/template-builder/fields/RichTextingCtaItem.vue';
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import { useI18n } from 'vue-i18n';
import { store } from '@/store';
import { createTranslationIdentifier } from '../utils/translate';
import { isRcsSmartListEnabled } from '../getters/rcs-smart-product-list';

interface CtaData {
  translationId: string | null;
  value: string;
  action: {
    type: string;
    value: string;
  };
  description?: string;
}

export default defineComponent({
  name: 'RichTextingCtasModal',

  components: {
    Dialog,
    SpmButton,
    RichTextingCtaItem,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    configs: {
      type: Object,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'update:visible': Boolean,
    'on-change-properties': Object,
  },

  setup(props, { emit }) {
    const { t } = useI18n();
    const localCtasData = ref<CtaData[]>([]);

    const ctaTypeOptions = computed(() => props.configs.options.ctaTypeOptions);

    // Count buttons by type
    const buttonCounts = computed(() => {
      const counts = {
        callNumber: 0,
        webAddress: 0,
        total: localCtasData.value.length,
      };

      localCtasData.value.forEach((cta) => {
        if (cta.action.type === LinkTypeEnum.CALL_NUMBER) {
          counts.callNumber += 1;
        } else if (cta.action.type === LinkTypeEnum.WEB_ADDRESS) {
          counts.webAddress += 1;
        }
      });

      return counts;
    });

    // Check if add button should be disabled
    const isAddButtonDisabled = computed(() => {
      if (isRcsSmartListEnabled() && buttonCounts.value.total >= 1) {
        return true;
      }
      if (buttonCounts.value.total >= 3) {
        return true;
      }
      if (buttonCounts.value.callNumber >= 1 && buttonCounts.value.webAddress >= 2) {
        return true;
      }
      return false;
    });

    // Generate appropriate limit message
    const limitReachedMessage = computed(() => {
      if (isRcsSmartListEnabled() && buttonCounts.value.total >= 1) {
        return t('templateBuilder.richTexting.maxButtonsSmartlist');
      }
      if (buttonCounts.value.total >= 3) {
        return t('templateBuilder.richTexting.maxButtonsReached');
      }
      if (buttonCounts.value.callNumber >= 1 && buttonCounts.value.webAddress >= 2) {
        return t('templateBuilder.richTexting.maxButtonTypesReached');
      }
      return null;
    });

    const originCall = computed(() => {
      let returnValue = 'templateBuilder';

      if (TemplateEditorState.template?.createdFromCampaign) {
        if ((new RegExp('_automation')).test(TemplateEditorState.template?.createdFromCampaign)) {
          returnValue = 'automation';
        } else {
          returnValue = 'newsletter';
        }
      }
      return returnValue;
    });

    const idCampaign = computed(() => TemplateEditorState.template?.idCampaign ?? undefined);

    // Watch for visibility changes to sync data when modal opens
    watch(
      () => props.visible,
      (isVisible) => {
        if (isVisible) {
          // Sync data from props when modal opens
          const propValue = props.parserValues.properties[0].value;
          if (Array.isArray(propValue)) {
            localCtasData.value = JSON.parse(JSON.stringify(propValue));
          }
        }
      },
      { immediate: true },
    );

    const emitChanges = () => {
      const updatedProperty: Property = {
        ...props.parserValues.properties[0],
        value: localCtasData.value,
      };
      emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [updatedProperty],
      });
    };

    const addCta = () => {
      if (!isAddButtonDisabled.value) {
        localCtasData.value.push({
          translationId: createTranslationIdentifier(),
          value: t('templateBuilder.richTexting.newAction'),
          action: {
            type: LinkTypeEnum.QUICK_REPLY,
            value: 'reply:quick_close',
          },
        });
        emitChanges();
      }
    };

    const deleteCta = (index: number) => {
      if (localCtasData.value.length > 0 && index >= 0 && index < localCtasData.value.length) {
        localCtasData.value.splice(index, 1);
        emitChanges();
      }
    };

    const updateCtaValue = (index: number, newValue: string) => {
      if (index >= 0 && index < localCtasData.value.length) {
        localCtasData.value[index].value = newValue;
        emitChanges();
      }
    };

    const updateCtaAction = (index: number, newAction: { type: string; value: string }) => {
      if (index >= 0 && index < localCtasData.value.length) {
        localCtasData.value[index].action = { ...newAction };
        emitChanges();
      }
    };

    const updateCtaDescription = (index: number, newDesc: string) => {
      if (index >= 0 && index < localCtasData.value.length) {
        localCtasData.value[index].description = newDesc;
        emitChanges();
      }
    };

    const getDisabledOptionsForCta = (ctaIndex: number): LinkTypeEnum[] => {
      const currentCta = localCtasData.value[ctaIndex];
      const currentType = currentCta?.action.type;
      const disabledOptions: LinkTypeEnum[] = [];

      ctaTypeOptions.value.forEach((option: string) => {
        if (option === currentType) {
          return;
        }

        if (option === LinkTypeEnum.CALL_NUMBER && buttonCounts.value.callNumber >= 1) {
          disabledOptions.push(option as LinkTypeEnum);
        } else if (option === LinkTypeEnum.WEB_ADDRESS && buttonCounts.value.webAddress >= 2) {
          disabledOptions.push(option as LinkTypeEnum);
        }
      });

      return disabledOptions;
    };

    const handleClose = () => {
      emit('update:visible', false);
      // Refresh configuration panel to update smartlist disable state based on CTA count
      store.commit('liveEditor/refreshConfigurationPanelKey');
    };

    return {
      t,
      localCtasData,
      ctaTypeOptions,
      idCampaign,
      originCall,
      isAddButtonDisabled,
      limitReachedMessage,
      addCta,
      deleteCta,
      updateCtaValue,
      updateCtaAction,
      updateCtaDescription,
      getDisabledOptionsForCta,
      handleClose,
    };
  },
});
